// src/components/ObbligoPresenze.js
// ═══════════════════════════════════════════════════════════════════════════
// «PRIMA METTI IN REGOLA IL CARTELLINO» (ottobre 2026, W419) — regole in utils/obbligoPresenze.js
// ═══════════════════════════════════════════════════════════════════════════
// useObbligoPresenze(): i giorni da mettere in regola della persona collegata, il livello
// scelto dall'azienda e dove lavora oggi. App.js mostra SchermataObbligoPresenze al posto
// di qualunque pagina, tranne Presenze (dove si timbra e si corregge: lì compare la versione
// compatta in cima) e Profilo. Titolare, admin e Platform Owner non vengono mai fermati.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { usePiano } from "../utils/usePiano";
import { statoModulo } from "../utils/pianiNexiora";
import { usePeriodoLavorativo, dataStr } from "../utils/periodoLavorativo";
import {
  GIORNI_INDIETRO, livelloObbligo, conSchermata, giorniDaSistemare, bloccante, idCartellino, idProgrammazione,
  celleDaProgrammazione, ASSENZE_PROGRAMMAZIONE, dataLungaInizio, TESTO_MANCANZA,
} from "../utils/obbligoPresenze";
import { inviaGiustificazione, MOTIVO_MAX, MOTIVO_MIN } from "../utils/giustificazioniPresenze";

const VIOLA = "#7c3aed", SCURO = "#1e1b4b", GRIGIO = "#6b7280";

export function useObbligoPresenze() {
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const uid = currentUser?.uid || "";
  const esente = !uid || !orgId || isOrgAdmin || isPlatformOwner || !!userProfile?.isDemoVisitor || orgId === "demo-nexiora-org";
  const { stato: statoPianoOrg } = usePiano(orgId);
  const moduloOk = statoModulo("squadra", statoPianoOrg) === "full";
  const [org, setOrg] = useState(null);
  useEffect(() => {
    if (esente) { setOrg(null); return undefined; }
    return onSnapshot(doc(db, "organizations", orgId), s => setOrg(s.exists() ? s.data() : null), () => setOrg(null));
  }, [orgId, esente]);
  const livello = esente || !moduloOk ? "spento" : livelloObbligo(org);
  const attivo = livello !== "spento";

  // giorno corrente: cambia a mezzanotte senza ricaricare
  const [oggiS, setOggiS] = useState(() => dataStr(new Date()));
  useEffect(() => { const t = setInterval(() => setOggiS(dataStr(new Date())), 60000); return () => clearInterval(t); }, []);
  const oggi = useMemo(() => { const [a, m, g] = oggiS.split("-").map(Number); return new Date(a, m - 1, g); }, [oggiS]);

  // documenti coinvolti: cartellini dei mesi e programmazioni delle settimane degli ultimi giorni (e di oggi)
  const { idCart, idProg } = useMemo(() => {
    const c = new Set(), p = new Set();
    for (let n = 0; n <= GIORNI_INDIETRO; n++) {
      const d = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate() - n);
      if (n > 0) c.add(idCartellino(orgId, uid, d));
      p.add(idProgrammazione(orgId, d));
    }
    return { idCart: [...c].sort(), idProg: [...p].sort() };
  }, [oggi, orgId, uid]);
  const chiaveCart = idCart.join("|"), chiaveProg = idProg.join("|");

  const [cartellini, setCartellini] = useState({});
  useEffect(() => {
    if (!attivo) { setCartellini({}); return undefined; }
    const stop = chiaveCart.split("|").map(id => onSnapshot(doc(db, "presenze", id),
      s => setCartellini(p => ({ ...p, [id]: s.exists() ? (s.data().giorni || {}) : {} })),
      () => setCartellini(p => ({ ...p, [id]: {} }))));
    return () => stop.forEach(f => f());
  }, [chiaveCart, attivo]);

  const [programmazioni, setProgrammazioni] = useState({});
  useEffect(() => {
    if (!attivo) { setProgrammazioni({}); return undefined; }
    const stop = chiaveProg.split("|").map(id => onSnapshot(doc(db, "programmazioniSettimanali", id),
      s => setProgrammazioni(p => ({ ...p, [id]: s.exists() ? s.data() : null })),
      () => setProgrammazioni(p => ({ ...p, [id]: null }))));
    return () => stop.forEach(f => f());
  }, [chiaveProg, attivo]);

  const [giustificazioni, setGiustificazioni] = useState(null);
  useEffect(() => {
    if (!attivo) { setGiustificazioni(null); return undefined; }
    return onSnapshot(query(collection(db, "giustificazioniPresenze"), where("orgId", "==", orgId), where("uid", "==", uid)),
      s => setGiustificazioni(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setGiustificazioni([]));
  }, [orgId, uid, attivo]);

  const periodo = usePeriodoLavorativo(attivo ? orgId : null, attivo ? uid : null);

  const celle = useMemo(() => Object.values(programmazioni).flatMap(p => celleDaProgrammazione(p)), [programmazioni]);
  const pronto = attivo && periodo.pronto && giustificazioni !== null &&
    idCart.every(id => id in cartellini) && idProg.every(id => id in programmazioni);
  const giorni = useMemo(() => (pronto ? giorniDaSistemare({
    oggi, livello, dal: org?.obbligoPresenzeDal || "", periodo, cartellini, celle, giustificazioni, uid, orgId,
  }) : []), [pronto, oggi, livello, org?.obbligoPresenzeDal, periodo, cartellini, celle, giustificazioni, uid, orgId]);

  const oggiQui = useMemo(() => celle.filter(c => c.uid === uid && c.data === oggiS), [celle, uid, oggiS]);
  return {
    livello, pronto, giorni, oggiQui, celle, orgId, uid, nome: userProfile?.displayName || currentUser?.displayName || "",
    blocca: pronto && bloccante(livello, giorni),
    cartelliniGiorno: (g) => (cartellini[idCartellino(orgId, uid, g.giorno)] || {})[String(g.giorno.getDate())] || null,
  };
}

/** «Oggi sei a: …» dalla programmazione (anche durante il blocco: il lavoro non si ferma). */
function OggiSeiA({ oggiQui }) {
  const lavoro = oggiQui.filter(c => !ASSENZE_PROGRAMMAZIONE.includes(c.commessaNome));
  if (!oggiQui.length) return null;
  return (
    <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#065f46" }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: .4, textTransform: "uppercase", marginBottom: 3 }}>📍 Oggi</div>
      {(lavoro.length ? lavoro : oggiQui).map((c, i) => (
        <div key={i}><b>{c.commessaNome}</b>{c.mezzo ? <span> · 🚐 {c.mezzo}</span> : null}</div>
      ))}
    </div>
  );
}

function RigaGiorno({ g, livello, onApriCartellino, invia, compatta }) {
  const [scrivi, setScrivi] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState("");
  const giust = g.giustificazione;
  const manda = async () => {
    setErrore(""); setInvio(true);
    try { await invia(g, motivo); setScrivi(false); setMotivo(""); }
    catch (e) { setErrore(e?.message || "Giustificazione non inviata: riprova."); }
    finally { setInvio(false); }
  };
  const btn = (primario) => ({ padding: "7px 12px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
    border: primario ? "none" : `1px solid ${VIOLA}`, background: primario ? VIOLA : "#fff", color: primario ? "#fff" : VIOLA });
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: compatta ? "8px 12px" : "12px 14px", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <b style={{ fontSize: 14, color: SCURO }}>{dataLungaInizio(g.giorno)}</b>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#fee2e2", color: "#b91c1c" }}>{TESTO_MANCANZA[g.mancanza]}</span>
        {g.stato === "in_attesa" && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#fef3c7", color: "#92400e" }}>⏳ giustificazione in attesa di approvazione</span>}
        {g.stato === "a_posto" && giust && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#ecfdf5", color: "#047857" }}>✓ giustificata</span>}
      </div>
      {g.stato === "respinta" && giust && (
        <div style={{ fontSize: 12.5, color: "#b91c1c", marginTop: 6 }}>Giustificazione respinta{giust.decisoDaNome ? ` da ${giust.decisoDaNome}` : ""}: «{giust.notaDecisione}»</div>
      )}
      {g.stato === "in_attesa" && giust && (
        <div style={{ fontSize: 12.5, color: GRIGIO, marginTop: 6 }}>Hai scritto: «{giust.motivo}». Puoi anche inserire gli orari, se li ricordi.</div>
      )}
      {(g.stato === "da_fare" || g.stato === "respinta" || g.stato === "in_attesa") && !scrivi && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {g.correggibile
            ? (compatta
              ? <span style={{ fontSize: 12, color: GRIGIO, alignSelf: "center" }}>Inserisci gli orari nel cartellino qui sotto, oppure</span>
              : <button type="button" style={btn(true)} onClick={onApriCartellino}>Inserisco gli orari</button>)
            : <span style={{ fontSize: 12, color: GRIGIO, alignSelf: "center" }}>Non puoi più correggerlo tu: scrivi il motivo, poi lo corregge chi approva i cartellini.</span>}
          {g.stato !== "in_attesa" && <button type="button" style={btn(!g.correggibile)} onClick={() => setScrivi(true)}>Giustifica</button>}
        </div>
      )}
      {scrivi && (
        <div style={{ marginTop: 10 }}>
          <label htmlFor={`giust-${g.data}`} style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Perché manca la timbratura?</label>
          <textarea id={`giust-${g.data}`} value={motivo} maxLength={MOTIVO_MAX} onChange={e => setMotivo(e.target.value)} rows={3}
            placeholder="Es. ero dal cliente fino a tardi e ho dimenticato di timbrare l'uscita alle 17:30"
            style={{ width: "100%", boxSizing: "border-box", marginTop: 4, padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, fontFamily: "inherit", resize: "vertical" }} />
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>La leggono chi approva i cartellini, il titolare e il PM del cantiere. Per una malattia usa «Registra assenza» nel cartellino: è un dato riservato.</div>
          {errore && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>{errore}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" style={{ ...btn(true), opacity: invio || motivo.trim().length < MOTIVO_MIN ? .5 : 1 }} disabled={invio || motivo.trim().length < MOTIVO_MIN} onClick={manda}>
              {invio ? "Invio…" : livello === "severa" ? "Invia a chi approva" : "Invia giustificazione"}
            </button>
            <button type="button" style={btn(false)} onClick={() => { setScrivi(false); setErrore(""); }}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * La schermata (compatta = in cima alla pagina Presenze).
 * @param stato - il risultato di useObbligoPresenze()
 */
export function SchermataObbligoPresenze({ stato, onApriCartellino, compatta = false }) {
  const { livello, giorni, oggiQui, celle, orgId, uid, nome } = stato;
  const daMostrare = giorni.filter(g => g.stato !== "a_posto");
  if (!daMostrare.length) return null;
  const invia = async (g, motivo) => {
    const giorno = stato.cartelliniGiorno(g);
    const commesseIds = [
      ...celle.filter(c => c.uid === uid && c.data === g.data && c.commessaId).map(c => c.commessaId),
      ...((giorno?.timbrature) || []).map(t => t.commessaId).filter(Boolean),
    ];
    await inviaGiustificazione({ orgId, utente: { uid, nome }, giorno: { data: g.data, mancanza: g.mancanza }, motivo, commesseIds });
  };
  const titolo = daMostrare.length === 1 ? "Un giorno del cartellino è da mettere in regola" : `${daMostrare.length} giorni del cartellino sono da mettere in regola`;
  const spiega = livello === "severa"
    ? "Inserisci gli orari o scrivi il motivo. Con il motivo, Nexiora si sblocca quando chi approva i cartellini lo accetta."
    : "Inserisci gli orari o scrivi il motivo: fatto questo, usi Nexiora normalmente.";
  if (compatta) return (
    <div role="region" aria-label="Cartellino da mettere in regola" style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: "12px 14px", margin: "0 0 14px", display: "grid", gap: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#9a3412" }}>🕐 {titolo}</div>
      <div style={{ fontSize: 12.5, color: "#9a3412" }}>{spiega} Per inserire gli orari tocca il giorno nel cartellino qui sotto.</div>
      {daMostrare.map(g => <RigaGiorno key={g.data} g={g} livello={livello} compatta invia={invia} onApriCartellino={onApriCartellino} />)}
    </div>
  );
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", background: "#f8f7fc", padding: "56px 16px 40px", boxSizing: "border-box" }}>
      <div role="alertdialog" aria-labelledby="obbligo-titolo" style={{ maxWidth: 560, margin: "0 auto", display: "grid", gap: 12 }}>
        <div style={{ fontSize: 40, lineHeight: 1 }}>🕐</div>
        <h1 id="obbligo-titolo" style={{ fontSize: 22, fontWeight: 800, color: SCURO, margin: 0 }}>{titolo}</h1>
        <p style={{ fontSize: 14.5, color: "#4b5563", margin: 0, lineHeight: 1.55 }}>{spiega}</p>
        <OggiSeiA oggiQui={oggiQui} />
        {daMostrare.map(g => <RigaGiorno key={g.data} g={g} livello={livello} invia={invia} onApriCartellino={onApriCartellino} />)}
        <button type="button" onClick={onApriCartellino}
          style={{ justifySelf: "start", padding: "9px 14px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          🕐 Apri il mio cartellino (timbra oggi)
        </button>
      </div>
    </div>
  );
}

/** Pagine che restano sempre aperte: il cartellino (si timbra e si corregge) e il Profilo. */
const PAGINE_LIBERE = ["presenze", "settings"];

/** Avvolge la pagina corrente in App.js (dentro OrgProvider). */
export function ZonaObbligoPresenze({ page, onNavigate, children }) {
  const stato = useObbligoPresenze();
  const apri = () => onNavigate && onNavigate({ page: "presenze" });
  if (stato.blocca && !PAGINE_LIBERE.includes(page)) return <SchermataObbligoPresenze stato={stato} onApriCartellino={apri} />;
  if (page === "presenze" && stato.pronto && conSchermata(stato.livello) && stato.giorni.some(g => g.stato !== "a_posto")) {
    return (<>
      <div style={{ flexShrink: 0, maxHeight: "45vh", overflowY: "auto", margin: "10px 16px 0" }}>
        <SchermataObbligoPresenze stato={stato} compatta onApriCartellino={() => {}} />
      </div>
      {/* la pagina sotto prende lo spazio che resta (le pagine usano height: 100%) */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{children}</div>
    </>);
  }
  return children;
}
